import React, { useState, useEffect, forwardRef } from 'react';
import { useSelector } from "react-redux";
import moment from 'moment-timezone';
import ReactHighcharts from 'highcharts-react-official';
import Highcharts from 'highcharts';
import HighchartsDrilldown from 'highcharts/modules/drilldown.src';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsNoData from 'highcharts/modules/no-data-to-display';
import { isPlannedOrTodate, isToday, isYesterday, isCustom, groupBy, getTimeXaxisCategories } from '../../../helpers/common';
import { DISPLAY_DATE_FORMAT2, DISPLAY_DATE_FORMAT3 } from '../../../constants';
import { Unit, formatNumber } from '../../../helpers/unit';
require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
HighchartsDrilldown(Highcharts);
HighchartsExport(Highcharts);
HighchartsNoData(Highcharts);

export const getFileName = props => {
  const { farmName, search: { range, custom } } = props;

  let dateRange = '';
  if (isPlannedOrTodate(range)) dateRange = range;
  else {
    const from = moment.utc(custom.from);
    const to = moment.utc(custom.to);
    if (to.diff(from, "hours") <= 24) dateRange = `${from.format(DISPLAY_DATE_FORMAT2)}`;
    else dateRange = `from ${from.format(DISPLAY_DATE_FORMAT2)} to ${to.format(DISPLAY_DATE_FORMAT2)}`;
  }

  return `${farmName} loads ${dateRange}`;
};

const DrillDownGraphForTotalLoads = forwardRef((props, chartRef) => {
  const { isRequesting, onDrilldown, onDrillup } = props
  const farmName = useSelector(state => state.farm.currentFarm.farm_name);
  const loadHistory = useSelector(state => state.dashboard.loads.loadHistory);
  const chartViewType = useSelector(state => state.dashboard.loads.chartViewType);
  const view = useSelector(state => state.dashboard.loads.viewTypes.filter(e => e.id === state.dashboard.loads.viewId)[0]);
  const settings = useSelector(state => state.user.settings);
  const { range, rangeId, custom: { from, to } } = useSelector(state => state.analyze.searchParams);
  const unit = Unit.getInstance();
  const chartProps = {
    farmName: farmName, search: { range: range, custom: { from, to } }
  }

  const getTotalCapacity = (list) => {
    const totalCapacity = list.reduce((capacity, item) => capacity + unit.toWeight(item.loadCapacity, 2), 0);
    return unit.toFixedOf(totalCapacity, 2);
  }

  const [drilldownId, setDrillDownId] = useState(null);
  const [chart, setChart] = useState(null);
  const [mainLoads, setMainLoads] = useState([]);
  const [displayType, setDisplayType] = useState(null);

  const [options, setOptions] = useState({
    credits: {
      enabled: false,
    },
    lang: {
      loading: "Loading...",
      noData: "No data found"
    },
    noData: {
      style: {
        fontSize: '15px',
        color: '#aca7a7'
      }
    },
    loading: {
      style: {
        fontSize: '15px',
        color: '#2a2929',
        marginTop: "25px"
      }
    },
    chart: {
      type: "column",
      events: {
        load: function () {
          setChart(this)
        }
      }
    },
    colors: ['#2b908f', '#90ee7e', '#f45b5b', '#7798BF', '#aaeeee', '#ff0066', '#eeaaee', '#55BF3B', '#DF5353', '#7798BF', '#aaeeee'],
    title: {
      text: null,
      style: {
        fontSize: "17px",
      }
    },
    accessibility: {
      announceNewData: {
        enabled: true
      }
    },
    xAxis: {
      type: 'category'
    },
    legend: {
      align: 'left',
      x: 50,
      verticalAlign: 'top',
      y: 40,
      floating: true,
      borderColor: '#CCC',
      borderWidth: 1,
      shadow: false
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        dataLabels: {
          enabled: true
        }
      }
    },
    tooltip: {
      enabled: true,
      headerFormat: undefined,
      pointFormat: '<span style="color:{point.color}">{point.name}</span>' +
        '<br><span>Crew</span>: <b>{series.name}</b><br />' +
        '<br><span>Loads</span>: <b>{point.count}</b><br />' +
        '<br><span>Weight</span>: <b>{point.weight}</b> {point.unit}<br/>'
    },
    series: [],
    drilldown: {
      series: []
    },
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
            'downloadCSV'
          ]
        }
      }
    },
  });

  const removeSeries = () => {
    if (!chart || !chart.series) return;
    const seriesLength = chart.series.length;
    for (let i = 0; i < seriesLength; i++) chart.series[0].remove(true);
  }

  useEffect(() => {
    const element = document.getElementById("dashboard-drill-down-chart");
    if (element && element.children && element.children.length !== 0)
      element.children[0].setAttribute("class", "drill-down-highchart");
  }, []);

  useEffect(() => {
    if (!chart || !chart.series) return;
    if (isRequesting) {
      removeSeries();
      chart.hideNoData();
      chart.showLoading();
    }
  }, [isRequesting, chart]);

  useEffect(() => {
    const isSameDay = moment.utc(from).startOf("day").valueOf() === moment.utc(to).startOf("day").valueOf();
    if (isToday(range) || isYesterday(range) || (isCustom(range) && isSameDay)) setDisplayType("time");
    else setDisplayType("date");
  }, [rangeId, from, to])

  useEffect(() => {
    window.__displayType = displayType;
    setDrillDownId(null);
    if (loadHistory.isRequesting || displayType === null) return;

    const loadsByCrewId = groupBy('crewUuid')(loadHistory.payload);
    const mainLoads = Object.keys(loadsByCrewId).map(crewUuid => {
      const crewLoads = loadsByCrewId[crewUuid] || [];
      const crewId = crewUuid === "null" ? 'Unknown' : crewUuid;

      let loads = {};
      if (displayType === "time") {
        loads = crewLoads.reduce((objectsByKeyValue, obj) => {
          const value = `${moment.utc(obj.loadDate).endOf("hour").add(1, "seconds").format("h A")}`;
          objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
          return objectsByKeyValue;
        }, {});
      } else {
        loads = crewLoads.reduce((objectsByKeyValue, obj) => {
          const value = moment.utc(obj.loadDate).format(DISPLAY_DATE_FORMAT3);
          objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
          return objectsByKeyValue;
        }, {});
      }

      const getCrewName = id => ((loadHistory.payload.find(load => load.crewUuid === id) || {}).crewName || 'Unknown');
      return { crewName: getCrewName(crewUuid), crewUuid: crewId, loads }
    });

    setMainLoads(displayType === "time" ? formatMainLoads(mainLoads) : mainLoads);
  }, [loadHistory.payload, displayType]);

  function formatMainLoads(load) {
    const result = [];
    let tempResult = [];
    for (var i = 0; i < load.length; i++) {
      const loadTimes = load[i] && load[i].loads ? Object.keys(load[i].loads).sort((a, b) => new Date(`2023-01-01 ${a}`).valueOf() - new Date(`2023-01-01 ${b}`).valueOf())
        : [];
      if (loadTimes.length === 0) continue
      tempResult.push({
        crewName: load[i].crewName,
        crewUuid: load[i].crewUuid,
        load_time: loadTimes[0]
      });
    }

    tempResult = tempResult.sort((a, b) => new Date(`2023-01-01 ${a.load_time}`).valueOf() - new Date(`2023-01-01 ${b.load_time}`).valueOf())
    for (const item of tempResult) {
      let res = load.find(e => e.crewUuid === item.crewUuid);
      result.push(res);
    }
    return result;
  }

  function formatSubLoads(load) {
    const result = [];
    let tempResult = [];
    for (var i = 0; i < load.length; i++) {
      const loadTimes = load[i] && load[i].data ? load[i].data.map((e) => e.id).sort((a, b) => new Date(`2023-01-01 ${a}`).valueOf() - new Date(`2023-01-01 ${b}`).valueOf())
        : [];
      if (loadTimes.length === 0) continue
      tempResult.push({
        name: load[i].name.toString().split(" ").join("-"),
        id: load[i].id,
        load_time: loadTimes[0]
      });
    }

    tempResult = tempResult.sort((a, b) => new Date(`2023-01-01 ${a.load_time}`).valueOf() - new Date(`2023-01-01 ${b.load_time}`).valueOf())
    for (const item of tempResult) {
      let res = load.find(e => e.name.toString().split(" ").join("-") === item.name);
      if (!res) continue;
      res.data.sort((a, b) => new Date(`2023-01-01 ${a.id}`).valueOf() - new Date(`2023-01-01 ${b.id}`).valueOf())
      result.push(res);
    }
    return result;
  }

  const getloadSeries = (seriesData) => {
    const updatedSeries = seriesData.map(it => {
      const existingIds = it.data.map(time => time.id);
      getTimeXaxisCategories().forEach(xAxis => {
        if (!existingIds.includes(xAxis)) {
          it.data.push({
            id: xAxis,
            name: xAxis,
            loadDate: null,
            y: null,
            color: null,
            count: 0,
            weight: 0,
            unit: null
          });
        }
      });

      return it;
    });
    return updatedSeries;
  }

  const getMainSeries = (series) => {
    getloadSeries(series).map(it => {
      const sortedData = it.data.sort((a, b) => moment(a.id, 'h A') - moment(b.id, 'h A'));
      const firstItem = sortedData.shift();
      sortedData.push(firstItem);
      return sortedData;
    });
  }

  useEffect(() => {
    const seriesData = mainLoads.map(item => ({
      name: item.crewName,
      color: null,
      data: getDrillUpData(item.loads, displayType, chartViewType, item.crewName)
    }));
    if (displayType === "time") getMainSeries(seriesData);
    const opt = {
      ...options,
      title: {
        ...options.title,
        text: `${view.name}`
      },
      yAxis: {
        stackLabels: {
          enabled: true
        },
        title: {
          text: chartViewType === 1 ? 'Loads' : `Weight (${settings.weightShortName})`
        }
      },
      exporting: {
        ...options.exporting,
        filename: getFileName(chartProps)
      },
      chart: {
        ...options.chart,
        events: {
          drilldown(e) {
            const me = this;
            setDrillDownId(e.point.id);
            const series = mainLoads.map(item => getDrillDownData(e.point.id, item.loads || {}, window.__viewType, item.crewName))
              .filter(it => it.name === e.point.crewName);
            const modifiedSeries = displayType === "date" ? formatSubLoads(series) : series;
            if (e.point.id) getMainSeries(modifiedSeries);
            modifiedSeries.forEach(data => { me.addSingleSeriesAsDrilldown(e.point, data) });
            me.applyDrilldown();
            return onDrilldown(e);
          },
          drillup(e) {
            setDrillDownId(null);
            return onDrillup(e);
          },
          load: function () {
            setChart(this)
          }
        }
      },
      series: seriesData,
      drilldown: {
        series: []
      }
    };

    setOptions(opt);
  }, [mainLoads]);

  useEffect(() => {
    window.__viewType = chartViewType;

    if (!chart) return;
    removeSeries();
    chart.yAxis[0].axisTitle.attr({
      text: chartViewType === 1 ? 'Loads' : `Weight (${settings.weightShortName})`
    });

    if (drilldownId) {
      const seriesData = mainLoads.map(item => getDrillDownData(drilldownId, item.loads || {}, chartViewType, item.crewName));
      if (displayType === "time") getMainSeries(seriesData);
      seriesData.forEach(series => chart.addSeries(series));
    } else {
      const seriesData = mainLoads.map(item => ({
        name: item.crewName,
        color: null,
        data: getDrillUpData(item.loads, displayType, chartViewType, item.crewName)
      }));
      if (displayType === "time") getMainSeries(seriesData);
      seriesData.forEach(series => chart.addSeries(series));
    }
  }, [chartViewType]);
  const getDrillUpData = (mainLoads, displayType, chartViewType, crewName) => {
    const data = Object.keys(mainLoads).map(id => {
      const totalWeight = getTotalCapacity(mainLoads[id]);
      return ({
        id,
        name: displayType === "time"
          ? id
          : id,
        loadDate: displayType === "time"
          ? moment.utc(mainLoads[id][0].loadDate).startOf("hour")
          : moment.utc(mainLoads[id][0].loadDate).startOf("day"),
        y: chartViewType === 1 ? mainLoads[id].length : totalWeight,
        drilldown: displayType === "time" ? null : id,
        color: null,
        count: mainLoads[id].length,
        weight: formatNumber(totalWeight),
        unit: settings.weightShortName,
        crewName
      })
    }).sort((a, b) => a.loadDate.valueOf() - b.loadDate.valueOf());

    return data;
  }

  const getDrillDownData = (drilldownId, mainLoads, chartViewType, seriesId) => {
    const subLoads = (mainLoads[drilldownId] || []).reduce((objectsByKeyValue, obj) => {
      const value = `${moment.utc(obj.loadDate).endOf("hour").add(1, "seconds").format("h A")}`;
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});
    const data = {
      id: seriesId,
      name: seriesId,
      color: null,
      data: Object.keys(subLoads)
        .map(subId => {
          const totalWeight = getTotalCapacity(subLoads[subId]);
          return ({
            id: subId,
            name: subId,
            y: chartViewType === 1 ? subLoads[subId].length : totalWeight,
            loadDate: subLoads[subId][0].loadDate,
            color: null,
            count: subLoads[subId].length,
            weight: formatNumber(totalWeight),
            unit: settings.weightShortName
          })
        }).sort((a, b) => b.loadDate.valueOf() - a.loadDate.valueOf())
    };

    return data;
  }

  return <ReactHighcharts ref={chartRef} immutable={true} highcharts={Highcharts} options={options} />;
});

export default DrillDownGraphForTotalLoads;
